import React from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import store from 'store';

import * as Config from '../../constants/Config';
import * as Util from '../../Util';
import firebase from '../../firebase';
import { firestore, copyRequestTemplatesUnderNewUser } from '../../firebase';
import * as User from '../../lib/User';
import * as Mailchimp from '../../api/Mailchimp';
import * as Onboarding from '../../analytics/Onboarding';
import FormikErrorTracker from '../../analytics/FormikErrorTracker';


const MIN_PASSWORD_LENGTH = 6;

class SignUpForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      formError: null,
      showPassword: false,
      passwordFocused: false
    };
  }

  togglePassword = () => {
    this.setState(prev => ({ showPassword: !prev.showPassword }));
  }

  friendlyErrorFor(errorCode, fallback) {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return { message: 'An account with this email already exists.', linkLabel: 'Sign in instead', linkTo: '/signin' };
      case 'auth/invalid-email':
        return { message: 'Please enter a valid email address.' };
      case 'auth/weak-password':
        return { message: `Password needs at least ${MIN_PASSWORD_LENGTH} characters.` };
      case 'auth/operation-not-allowed':
        return { message: 'Email sign-up is temporarily unavailable. Please try again soon.' };
      default:
        return { message: fallback || 'Something went wrong. Please try again.' };
    }
  }

  render() {
    const { formError } = this.state;

    return (
      <div>
        <Formik
          validateOnChange={false}
          initialValues={{ email: '', password: '', newsletter: true }}
          validate={values => {
            let errors = {};
            if (!values.email) {
              errors.email = 'Please enter your email address.';
            } else if (
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
            ) {
              errors.email = 'Invalid email address';
            }

            if (!values.password) {
              errors.password = 'Please enter the password.';
            } else if (values.password.length < MIN_PASSWORD_LENGTH) {
              errors.password = `Password needs at least ${MIN_PASSWORD_LENGTH} characters.`;
            }

            return errors;
          }}
          onSubmit={async (values, { setSubmitting }) => {
            this.setState({ formError: null });
            const email = values.email.trim().toLowerCase();
            const password = values.password;
            try {
              const data = await firebase.auth().createUserWithEmailAndPassword(email, password);
              const userId = data.user.uid;
              const { fullName, ...address } = store.get('userInfo');
              const createdAt = new Date();
              const numOfFreeRequest = Config.NUM_OF_FREE_REQUEST;
              const userData = {
                email, fullName, address, createdAt, numOfFreeRequest
              };
              await firestore.collection('users').doc(`${userId}`).set(userData);
              await copyRequestTemplatesUnderNewUser(userId, fullName, address);
              User.cacheCurrentUser(userData);

              if (Util.isProduction()) {
                Mailchimp.addSubscriber(fullName, email); // no need to await
                Mailchimp.sendNewUserNotification(fullName);
                window.amplitude.getInstance().logEvent('Signed up');
                window.gtag('event', 'sign_up', {
                  method: 'email'
                });
                window.gtag('event', 'ua_sign_up', {
                  method: 'email'
                });
                window.fbq('track', 'Lead', {
                  value: 2,
                  currency: 'USD',
                });
                window.mixpanel.identify(userId);
                window.mixpanel.people.set({
                  '$name': fullName,
                  '$email': email,
                });
                window.mixpanel.track('Sign Up', { method: 'email' });
                Onboarding.logCompletedStep(2, { method: 'email' });

                try {
                  window.FS.identify(userId, {
                    displayName: fullName,
                    email: email
                  });
                  window.FS.event('Sign-up');
                } catch(error) {
                  console.error(error);
                }
              }
              this.props.goToNextStep();
            } catch(error) {
              const errorCode = error.code;
              const errorMessage = error.message;
              this.setState({ formError: this.friendlyErrorFor(errorCode, errorMessage) });
            }
            setSubmitting(false);
          }}
        >
          {({ isSubmitting, errors, touched, values }) => (
            <Form>
              <FormikErrorTracker step={2} errors={errors} touched={touched} values={values} />
              <div className="field">
                <Field type="email" id="email" name="email" placeholder=" " autoComplete="email" autoCorrect="off" spellCheck="false" />
                <label htmlFor="email">Email</label>
                <ErrorMessage name="email" component="div" className="error" />
              </div>
              <div className="field password-field">
                <Field name="password">
                  {({ field }) => (
                    <input
                      {...field}
                      type={this.state.showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder=" "
                      autoComplete="new-password"
                      onFocus={() => this.setState({ passwordFocused: true })}
                      onBlur={e => {
                        this.setState({ passwordFocused: false });
                        field.onBlur(e);
                      }}
                    />
                  )}
                </Field>
                <label htmlFor="password">Password</label>
                <button
                  type="button"
                  className="password-toggle"
                  onClick={this.togglePassword}
                  aria-label={this.state.showPassword ? 'Hide password' : 'Show password'}
                >
                  {this.state.showPassword ? 'Hide' : 'Show'}
                </button>
                <ErrorMessage name="password" component="div" className="error" />
                {this.state.passwordFocused && (values.password || '').length < MIN_PASSWORD_LENGTH && !(touched.password && errors.password) && (
                  <p className="field-hint">At least {MIN_PASSWORD_LENGTH} characters.</p>
                )}
              </div>
              <div style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: 'var(--color-ink-muted)' }}>
                By clicking Create my account, you agree to our <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>.
              </div>
              {formError ? (
                <div className="form-error-message">
                  {formError.message}
                  {formError.linkTo ? (
                    <> <Link to={formError.linkTo}>{formError.linkLabel}</Link>.</>
                  ) : null}
                </div>
              ) : null}
              <button className="button submit-button submit-button--primary" type="submit" disabled={isSubmitting}>
                { isSubmitting ? 'Creating your account…' : 'Create my account' }
              </button>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default SignUpForm;
